import { useState, useEffect } from 'react';
import { useAuth, apiService } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { 
  Phone, MessageSquare, Search, Filter, RefreshCw, Download, MoreVertical,
  Trash2, Clock, MapPin, CheckCircle, AlertCircle, PhoneCall
} from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';

const statusOptions = [
  { value: 'new', label: 'New', color: 'bg-green-100 text-green-700', icon: AlertCircle },
  { value: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-700', icon: PhoneCall },
  { value: 'quoted', label: 'Quoted', color: 'bg-yellow-100 text-yellow-700', icon: MessageSquare },
  { value: 'closed', label: 'Closed', color: 'bg-slate-100 text-slate-700', icon: CheckCircle },
];

export const EnquiriesManager = () => {
  const { token } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [detailModal, setDetailModal] = useState({ open: false, item: null });

  useEffect(() => { fetchEnquiries(); }, [filterStatus]);

  const fetchEnquiries = async () => {
    try {
      const params = { limit: 100 };
      if (filterStatus !== 'all') params.status = filterStatus;
      if (search) params.search = search;
      const res = await apiService.getEnquiries(token, params);
      setEnquiries(res.data.enquiries);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchEnquiries();
  };

  const updateStatus = async (id, status) => {
    try {
      await apiService.updateEnquiry(token, id, { status });
      toast.success('Status updated');
      fetchEnquiries();
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const updateNotes = async (id, notes) => {
    try {
      await apiService.updateEnquiry(token, id, { notes });
      toast.success('Notes saved');
      fetchEnquiries();
    } catch (error) {
      toast.error('Error saving notes');
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteEnquiry(token, deleteDialog.item.id);
      toast.success('Enquiry deleted');
      setDeleteDialog({ open: false, item: null });
      fetchEnquiries();
    } catch (error) {
      toast.error('Error deleting enquiry');
    }
  };

  const exportEnquiries = async () => {
    try {
      const res = await apiService.exportEnquiries(token);
      const dataStr = JSON.stringify(res.data, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      const link = document.createElement('a');
      link.setAttribute('href', dataUri);
      link.setAttribute('download', `enquiries_${new Date().toISOString().split('T')[0]}.json`);
      link.click();
      toast.success('Enquiries exported');
    } catch (error) {
      toast.error('Error exporting');
    }
  };

  const getStatusConfig = (status) => statusOptions.find(s => s.value === status) || statusOptions[0];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const filtered = enquiries.filter(e => {
    if (!search) return true;
    return e.name.toLowerCase().includes(search.toLowerCase()) ||
           e.phone.includes(search) ||
           e.service_needed?.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="space-y-6" data-testid="enquiries-manager">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Oswald'] text-2xl font-bold text-slate-900">Enquiries / Leads</h1>
          <p className="text-slate-500">Manage customer enquiries and leads ({total} total)</p>
        </div>
        <Button onClick={exportEnquiries} variant="outline">
          <Download className="w-4 h-4 mr-2" /> Export
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statusOptions.map(status => {
          const count = enquiries.filter(e => e.status === status.value).length;
          return (
            <Card key={status.value} className={`cursor-pointer ${filterStatus === status.value ? 'ring-2 ring-sky-500' : ''}`} onClick={() => setFilterStatus(filterStatus === status.value ? 'all' : status.value)}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${status.color}`}>
                  <status.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-xs text-slate-500">{status.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, phone, or service..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statusOptions.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchEnquiries}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Enquiries List */}
      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No enquiries found</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((enquiry) => {
            const statusConfig = getStatusConfig(enquiry.status);
            return (
              <Card key={enquiry.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center text-sky-600 font-bold">
                          {enquiry.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{enquiry.name}</h3>
                          <p className="text-sky-600 text-sm flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {enquiry.phone}
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                      
                      {enquiry.service_needed && (
                        <p className="text-sm text-slate-700"><span className="text-slate-500">Service:</span> {enquiry.service_needed}</p>
                      )}
                      {enquiry.location && (
                        <p className="text-sm text-slate-600 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {enquiry.location}
                        </p>
                      )}
                      {enquiry.message && (
                        <p className="text-sm text-slate-600 mt-2 p-2 bg-slate-50 rounded italic">"{enquiry.message}"</p>
                      )}
                      {enquiry.notes && (
                        <p className="text-sm text-orange-600 mt-2 p-2 bg-orange-50 rounded">
                          <span className="font-medium">Notes:</span> {enquiry.notes}
                        </p>
                      )}
                      <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatDate(enquiry.created_at)} • via {enquiry.source}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Select value={enquiry.status} onValueChange={(value) => updateStatus(enquiry.id, value)}>
                        <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {statusOptions.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      
                      <a href={`tel:${enquiry.phone}`}>
                        <Button size="sm" className="bg-green-500 hover:bg-green-600">
                          <Phone className="w-4 h-4" />
                        </Button>
                      </a>
                      <a href={`https://wa.me/91${enquiry.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline" className="border-green-500 text-green-600">
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                      </a>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost"><MoreVertical className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setDetailModal({ open: true, item: enquiry })}>
                            View / Add Notes
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDeleteDialog({ open: true, item: enquiry })} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={detailModal.open} onOpenChange={(open) => !open && setDetailModal({ open: false, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enquiry Details</DialogTitle>
          </DialogHeader>
          {detailModal.item && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold">{detailModal.item.name}</h3>
                <p className="text-sky-600">{detailModal.item.phone}</p>
                {detailModal.item.service_needed && <p className="text-sm text-slate-600">Service: {detailModal.item.service_needed}</p>}
                {detailModal.item.location && <p className="text-sm text-slate-600">Location: {detailModal.item.location}</p>}
                {detailModal.item.message && <p className="text-sm mt-2 italic">"{detailModal.item.message}"</p>}
              </div>
              
              <div className="space-y-2">
                <Label>Add Notes</Label>
                <Textarea
                  value={detailModal.item.notes || ''}
                  onChange={(e) => setDetailModal({ ...detailModal, item: { ...detailModal.item, notes: e.target.value } })}
                  placeholder="Add internal notes about this enquiry..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailModal({ open: false, item: null })}>Close</Button>
            <Button onClick={() => { updateNotes(detailModal.item.id, detailModal.item.notes); setDetailModal({ open: false, item: null }); }} className="bg-sky-500 hover:bg-sky-600">
              Save Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Enquiry?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this enquiry. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EnquiriesManager;
